import {
    AccessSegment,
    ColumnMetadata,
    ColumnTypes,
} from "~/components/DataGrid";
import { Emdash } from "~/components/Punctuation";
import { getAssetURL } from "~/utils/getAssetURL";
import { Token, TokenColumns } from "~/modules/tokens/types";
import { Link } from "~/components/Link";
import { DataGridDollarPercentChangeComboCell } from "~/components/DataGrid/DataGridDollarPercentChangeComboCell";
import { getMeasureValue } from "~/utils/getMeasureValue";
import { DataGridNumberPercentChangeComboCell } from "~/components/DataGrid/DataGridNumberPercentChangeComboCell";
import { Networks } from "~/schema";
import { BlockchainExplorerLink } from "~/components/BlockchainExplorerLink";
import { ImageLoader } from "~/components/ImageLoader";
import { BROADRIDGE_ASSET_TICKER, RWA_COIN_ICON } from "~/consts";
import { TokenUserEvents } from "~/modules/analytics/events/tokens";
import { cn } from "~/lib/utils";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import { Icon } from "~/components/Icon";
import { getManagerURL, getNetworkURL, getPlatformURL } from "~/routes";
import { TokenizationTypeBadge } from "~/components/TokenizationTypeBadge";
import { CatalogReferenceDataSlugs } from "~/modules/catalog/types";

// // // //

export const ASSET_COLUMN: ColumnMetadata<Token, TokenColumns> = {
    label: "Product Name",
    slug: TokenColumns.asset_name,
    sortsOn: TokenColumns.asset_name,
    type: ColumnTypes.string,
    width: 250,
    sticky: {
        left: 0,
        isLast: true,
    },
    render: (item) => {
        const asset = item.asset;

        return (
            <Link
                href={getAssetURL({ asset })}
                variant="default"
                className="w-full flex items-center gap-1.5"
                analyticsLabel={TokenUserEvents.TableAssetLinkClick}
            >
                <ImageLoader
                    src={asset.icon_url || RWA_COIN_ICON}
                    alt={`${asset.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-5 w-5 min-w-5 rounded-full"
                />
                <span className="dg-overflow">{asset.name}</span>
            </Link>
        );
    },
};

// // // //

export const PROTOCOL_COLUMN: ColumnMetadata<Token, TokenColumns> = {
    label: "Platform",
    slug: TokenColumns.protocol_slug,
    sortsOn: TokenColumns.protocol_slug,
    type: ColumnTypes.string,
    width: 175,
    tooltip: CatalogReferenceDataSlugs.platform,
    render: (item) => {
        const protocol = item.protocol;

        if (!protocol) return <Emdash />;

        return (
            <Link
                href={getPlatformURL({ platformSlug: protocol.slug })}
                className="flex items-center gap-1 align-middle relative"
                variant="default"
                analyticsLabel={TokenUserEvents.TableProtocolLinkClick}
                analyticsEventProperties={{
                    protocol_slug: protocol.slug,
                }}
            >
                <ImageLoader
                    src={protocol.icon_url || RWA_COIN_ICON}
                    alt={`${protocol.name} icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-5 w-5 min-w-5 rounded-full"
                />
                <span className="dg-overflow">{protocol.name}</span>
            </Link>
        );
    },
};

// // // //

export const MANAGER_COLUMN: ColumnMetadata<Token, TokenColumns> = {
    label: "Asset Manager",
    slug: TokenColumns.manager_slug,
    sortsOn: TokenColumns.manager_slug,
    type: ColumnTypes.string,
    width: 180,
    tooltip: CatalogReferenceDataSlugs.manager,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        const manager = item.asset_manager;

        if (!manager) return <Emdash />;

        return (
            <Link
                href={getManagerURL({ managerSlug: manager.slug })}
                variant="default"
                className="w-full flex items-center gap-2"
                analyticsLabel={TokenUserEvents.TableManagerLinkClick}
                analyticsEventProperties={{ manager_slug: manager.slug }}
            >
                <ImageLoader
                    src={manager.icon_url || RWA_COIN_ICON}
                    alt={`${manager.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-5 w-5 min-w-5 rounded-full"
                    fallbackClassName="bg-white opacity-50"
                />
                <span className="dg-overflow">{manager.name}</span>
            </Link>
        );
    },
};

// // // //

export const NETWORK_COLUMN: ColumnMetadata<Token, TokenColumns> = {
    label: "Network",
    slug: TokenColumns.network_slug,
    sortsOn: TokenColumns.network_slug,
    type: ColumnTypes.string,
    width: 200,
    render: (item) => {
        const network = item.network;

        if (!network) return <Emdash />;

        return (
            <Link
                href={getNetworkURL({ networkSlug: network.slug })}
                variant="default"
                className="flex items-center gap-1 align-middle relative"
                analyticsLabel={TokenUserEvents.TableNetworkLinkClick}
                analyticsEventProperties={{
                    network_slug: network.slug,
                }}
            >
                <ImageLoader
                    src={network.icon_url || RWA_COIN_ICON}
                    alt={`${network.name} icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-5 w-5 min-w-5 rounded-full"
                />
                <span className="dg-overflow">{network.name}</span>
            </Link>
        );
    },
};

// // // //

export const TICKER_COLUMN: ColumnMetadata<Token, TokenColumns> = {
    slug: TokenColumns.asset_ticker,
    sortsOn: TokenColumns.asset_ticker,
    label: "Ticker",
    type: "string",
    width: 100,
    render: (item) => {
        const ticker = item.asset.ticker;

        // Hide the hardcoded-ticker for the Broadridge DLR Asset
        if (ticker === BROADRIDGE_ASSET_TICKER) {
            return <Emdash />;
        }

        return <span className="font-mono">{ticker}</span>;
    },
};

// // // //

export const ASSET_NAME_AND_TICKER_COLUMN: ColumnMetadata<Token, TokenColumns> =
    {
        ...ASSET_COLUMN,
        label: (
            <>
                <p>Name</p>
                <p className="text-xs">Ticker</p>
            </>
        ),
        render: (item, options) => (
            <div className="flex flex-col justify-center w-full overflow-hidden h-11">
                {ASSET_COLUMN.render?.(item, options)}
                <span className="font-light text-xs mt-0.5 text-muted-foreground dg-overflow font-mono">
                    {item.asset.ticker === BROADRIDGE_ASSET_TICKER ? (
                        <Emdash />
                    ) : (
                        item.asset.ticker || <Emdash />
                    )}
                </span>
            </div>
        ),
    };

// // // //

export const ASSET_CLASS_COLUMN: ColumnMetadata<Token, TokenColumns> = {
    slug: TokenColumns.asset_class_slug,
    sortsOn: TokenColumns.asset_class_slug,
    label: "Asset Class",
    type: "string",
    width: 215,
    tooltip: CatalogReferenceDataSlugs.asset_class,
    render: (item) => {
        if (item.asset_class === null) {
            return <Emdash />;
        }

        const { href, label, icon } = getAssetClassLinkMetadata(
            item.asset_class?.id,
        );

        if (href === null && label === null) {
            return <Emdash />;
        }

        if (href === null && icon === null) {
            return label;
        }

        if (href === null) {
            return (
                <div className="flex items-center gap-2">
                    <Icon className="h-3.5" icon={icon} />
                    {label}
                </div>
            );
        }

        return (
            <Link
                href={href}
                analyticsLabel={TokenUserEvents.TableAssetClassLinkClick}
                analyticsEventProperties={{
                    asset_class: item.asset_class?.slug,
                }}
                variant="default"
                className="flex items-center gap-2"
            >
                <Icon className="h-3.5" icon={icon} />
                {label}
            </Link>
        );
    },
};

// // // //

export const TOKEN_BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN: ColumnMetadata<
    Token,
    TokenColumns
> = {
    slug: TokenColumns.bridged_token_value_dollar,
    label: "Total Value",
    tooltip:
        "Total market value of all tokens, including authorized but unissued tokens.",
    type: ColumnTypes.dollar,
    sortsOn: TokenColumns.bridged_token_value_dollar,
    width: 175,
    render: (token) => {
        if (!token) {
            return <Emdash />;
        }

        const measure = getMeasureValue(token, "bridged_token_value_dollar");

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="7d"
                pct_change_value={measure.chg_7d_pct}
            />
        );
    },
};

// // // //

export const TOKENIZATION_TYPE_COLUMN: ColumnMetadata<Token, TokenColumns> = {
    label: (
        <>
            Tokenization
            <br />
            Type
        </>
    ),
    slug: "tokenization_type_id",
    // sortsOn: TokenColumns.tokenizationTypeID,
    type: ColumnTypes.string,
    width: 160,
    tooltip: CatalogReferenceDataSlugs.tokenization_type,
    render: (token) => {
        // Return Emdash if no token
        if (!token) return <Emdash />;

        // Return Emdash if no token.tokenization_type_id
        const tokenization_type_id = token.tokenization_type_id;
        if (!tokenization_type_id) return <Emdash />;

        return (
            <TokenizationTypeBadge
                tokenization_type_id={tokenization_type_id}
                size="sm"
            />
        );
    },
};

// // // //

export const TOKEN_HOLDERS_COLUMN: ColumnMetadata<Token, TokenColumns> = {
    slug: TokenColumns.holding_addresses_count,
    label: "Holders",
    tooltip: "The number of addresses that hold the token",
    type: ColumnTypes.number,
    sortsOn: TokenColumns.holding_addresses_count,
    width: 140,
    render: (token) => {
        const measure = getMeasureValue(token, "holding_addresses_count");

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridNumberPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

// // // //

export const INCORPORATION_COUNTRY_COLUMN: ColumnMetadata<Token, TokenColumns> =
    {
        slug: TokenColumns.issuer_legal_structure_country_name,
        label: "Domicile",
        tooltip: "The country in which the asset issuer is incorporated",
        sortsOn: TokenColumns.issuer_legal_structure_country_name,
        type: ColumnTypes.string,
        width: 220,
        access: {
            view: AccessSegment.Preview,
            sort: AccessSegment.Preview,
        },
        render: (token) => {
            if (!token.issuer || !token.issuer?.legal_structure_country) {
                return <Emdash />;
            }

            const {
                legal_structure_country,
                legal_structure_country_icon_url,
            } = token.issuer;

            return (
                <div className="flex items-center">
                    {legal_structure_country_icon_url && (
                        <ImageLoader
                            src={
                                legal_structure_country_icon_url ||
                                RWA_COIN_ICON
                            }
                            fallbackSrc={RWA_COIN_ICON}
                            fallbackClassName="opacity-50"
                            alt={`${legal_structure_country} Flag`}
                            className="w-5 rounded-xs mr-1.5 shadow-sm"
                        />
                    )}
                    <p className="dg-overflow">{legal_structure_country}</p>
                </div>
            );
        },
    };

// // // //

export const LICENSE_OR_REGULATORY_FRAMEWORK: ColumnMetadata<
    Token,
    TokenColumns
> = {
    slug: TokenColumns.asset_regulatory_framework,
    label: "Regulatory Framework",
    tooltip:
        "Inclusive of both standalone licenses and comprehensive regulatory frameworks",
    type: ColumnTypes.string,
    sortsOn: TokenColumns.asset_regulatory_framework,
    width: 675,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (token) => {
        // Render placeholder with fixed height so all rows are the same height
        if (!token.asset.governing_body && !token.asset.regulatory_framework) {
            return (
                <div className="flex text-muted-foreground h-[36px] items-center">
                    <Emdash />
                </div>
            );
        }

        return (
            <div
                className={cn("flex flex-col", {
                    "h-[36px]": !token.asset.governing_body,
                })}
            >
                <p className="text-foreground dg-overflow">
                    {token.asset.regulatory_framework}
                </p>
                {token.asset.governing_body && (
                    <p className="font-light text-xs text-muted-foreground">
                        {token.asset.governing_body || "N/A"}
                    </p>
                )}
            </div>
        );
    },
};

// // // //

export const TOKEN_CONTRACT_ADDRESS_COLUMN: ColumnMetadata<
    Token,
    TokenColumns
> = {
    slug: "token_address",
    label: "Contract Address",
    type: "string",
    width: 180,
    tooltip: CatalogReferenceDataSlugs.address,
    render: (token, { size }) => {
        const network = token.network.slug as Networks;

        return (
            <BlockchainExplorerLink
                address={token.address}
                networkSlug={network}
                analyticsLabel="Data Grid - Contract Address Link"
                size={size}
                type="token"
            />
        );
    },
};
